/**
 * Shared constants. Domain and schema values come straight from @castfeeds/domain and @castfeeds/schema
 * (side-effect free from 0.2.1; Ajv is only built when a validator runs). Web-only constants live below.
 */
import type { ApprovalStage, Channel } from '@castfeeds/domain';

export { CAPABILITIES, CHANNELS, DEFAULT_KIT_VALUES, EPISODE_STATUSES, ROLES } from '@castfeeds/domain';
export { BRAND_KIT_FIELDS, CAPTION_POSITIONS, CAPTION_PRESETS, CAPTION_SIZES, LOGO_CORNERS, LOWER_THIRD_STYLES, MUSIC_MOODS } from '@castfeeds/schema';
import { can as domainCan, canAssignRole as domainCanAssignRole, type Capability, type Role } from '@castfeeds/domain';

/** `can()` that tolerates a missing role (signed-out or loading). */
export function can(role: Role | undefined | null, capability: Capability): boolean {
  return !!role && domainCan(role, capability);
}

export function canAssignRole(actor: Role | undefined | null, target: Role): boolean {
  return !!actor && domainCanAssignRole(actor, target);
}

/** Channel → [name, short code, chip colour] (prototype CH). Chip text is always Ink on these light tints. */
export const CHANNEL_META: Record<Channel, readonly [string, string, string]> = {
  yt: ['YouTube', 'YT', '#FF5A5F'],
  yts: ['YouTube Shorts', 'YTS', '#FF5A5F'],
  ig: ['Instagram Reels', 'IG', '#F77FB0'],
  igs: ['Instagram Stories', 'IGS', '#F77FB0'],
  tt: ['TikTok', 'TT', '#5CE1E6'],
  fb: ['Facebook', 'FB', '#7FA8FF'],
  li: ['LinkedIn', 'IN', '#6FB3F2'],
  x: ['X', 'X', '#C9CCD2'],
  th: ['Threads', 'TH', '#C9CCD2'],
  pin: ['Pinterest', 'PIN', '#FF8A8A'],
  vimeo: ['Vimeo', 'VM', '#7FD8F5'],
  tg: ['Telegram', 'TG', '#8FD3F7'],
  wa: ['WhatsApp Channels', 'WA', '#7EE3A3'],
};

export const PROJECT_LANGUAGES = ['he', 'en', 'ar', 'ru'] as const;

export const COLOUR_ROLES = ['primary', 'ink', 'paper', 'secondary', 'highlight'] as const;
/** Fonts loaded in index.html; all cover Hebrew and Latin. Secular One has no body weight. */
export const HEADING_FONTS = ['Rubik', 'Heebo', 'Assistant', 'Frank Ruhl Libre', 'Secular One'] as const;
export const BODY_FONTS = ['Rubik', 'Heebo', 'Assistant', 'Frank Ruhl Libre'] as const;

/** Pipeline (contract §4.3/§4.5). */
export const STAGES = ['ingest', 'sync', 'transcribe', 'analyze', 'render', 'export'] as const;
/** Source kinds accepted by POST /sessions/{id}/uploads (amendment 1). */
export const SOURCE_KINDS = ['camera', 'mic', 'mix', 'program', 'screen', 'upload'] as const;
export const HOOK_STYLES = ['question', 'bold_claim', 'number', 'cold_open'] as const;
export const SOCIAL_MOODS = ['upbeat', 'hype', 'lofi', 'none'] as const;
export const PACES = ['calm', 'punchy', 'hyper'] as const;
export const RENDER_FORMATS = ['16:9', '9:16', '1:1'] as const;
/** "All formats" (amendment 11): sibling edits sharing one cut, in the order social platforms use them. */
export const ALL_FORMATS = ['9:16', '4:5', '1:1', '16:9'] as const;

/** Nav items outside Pilot 1 and the phase they arrive in (spec §MVP build plan / §Post-MVP). */
export const SOON: Record<string, 'P1' | 'P2'> = {
  insights: 'P2',
  localize: 'P2',
  player: 'P2',
};

/** The status pill for each approval stage (prototype ST map). */
export const STAGE_PILL: Record<ApprovalStage, string> = { none: 'draft', review: 'review', changes: 'changes', client: 'client', approved: 'approved' };

/** Score threshold for "Approve all ≥ …" (prototype). */
export const APPROVE_ALL_SCORE = 8.5;

/** Status polling interval for a processing session (contract §4.5: 3 s; SSE later). */
export const SESSION_POLL_MS = 3000;
/** Team → Devices refresh (the API counts a device online for 60 s after its heartbeat). */
export const DEVICES_POLL_MS = 15_000;
/** The pairing dialog checks whether the engine redeemed the code. */
export const PAIRING_POLL_MS = 2000;
/** Session poll while a Fix & enhance preview renders (a few seconds of work). */
export const PREVIEW_POLL_MS = 1500;
/** Tasks inbox, "My tasks" and the activity feed refresh (triggers open tasks in the background). */
export const TASKS_POLL_MS = 30_000;
/** Presence heartbeat when the API's answer did not say (it sends `heartbeat_s`, 10 s). */
export const PRESENCE_FALLBACK_MS = 10_000;
